import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewQCCheck = forwardRef(({ checkId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onCheckCreated }, ref) => {
  const { t } = useTranslation('qc');
  const { t: tCommon } = useTranslation('common');
  const [check, setCheck] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { showSuccess, showError } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (checkId && !createMode) {
      fetchCheck();
    } else if (createMode) {
      const newCheck = {
        check_type: 'INCOMING',
        status: 'PENDING',
        production_order_line_id: null,
        inventory_item_id: null,
        measurements: null,
        defects_found: '',
        corrective_action: '',
        notes: ''
      };
      setCheck(newCheck);
      setEditData(newCheck);
    }
  }, [checkId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchCheck = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/qc/checks/${checkId}`));
      if (response.ok) {
        const data = await response.json();
        setCheck(data);
        setEditData(data);
      } else {
        setError(t('checks.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching check:', error);
      setError(t('checks.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      if (!editData.check_type) {
        showError(t('checks.view.validation.required'));
        return;
      }

      // Empty texts go as null so the field is cleared (#367); the type is fixed once created
      const saveData = {
        check_type: editData.check_type,
        status: editData.status || 'PENDING',
        production_order_line_id: editData.production_order_line_id || null,
        inventory_item_id: editData.inventory_item_id || null,
        defects_found: editData.defects_found || null,
        corrective_action: editData.corrective_action || null,
        notes: editData.notes || null
      };

      // Include measurements if provided
      if (editData.measurements) {
        saveData.measurements = editData.measurements;
      }

      const url = createMode
        ? getApiUrl('/api/qc/checks')
        : getApiUrl(`/api/qc/checks/${checkId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('checks.view.messages.createdSuccess'));
          if (onCheckCreated) {
            onCheckCreated(result.check_id);
          }
        } else {
          showSuccess(t('checks.view.messages.updatedSuccess'));
          // Refetch to get the updated data
          await fetchCheck();
        }

        if (onSave) {
          onSave();
        }
      } else {
        await showApiError(response, showError, tCommon);
        return;
      }
    } catch (error) {
      console.error('Error saving check:', error);
      showError(t('checks.view.error.loadingError'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('checks.view.confirmations.unsavedChanges'))) {
        setEditData(check || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('checks.view.confirmations.deleteConfirm'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/qc/checks/${checkId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('checks.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
        return;
      }
    } catch (error) {
      console.error('Error deleting check:', error);
      showError(t('checks.view.error.fetchFailed'));
    }
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('checks.loading')}</div></div>;
  }

  if (error && !check) {
    const errorMessage = typeof error === 'string' ? error : 'An error occurred';
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!check && !createMode) {
    return <div className="view-container"><div className="no-data">{t('checks.view.titles.noCheckFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('checks.view.titles.newCheck') : `${t('checks.view.titles.viewCheck')} #${check?.check_id || ''}`}
          </h2>
          <span className="header-id">{!createMode && check?.check_type}</span>
        </div>
        <div className="header-status">
          {editData.status && (
            <span className={`status-badge ${
              editData.status === 'PASSED' ? 'status-active' :
              editData.status === 'FAILED' ? 'status-error' :
              'status-warning'
            }`}>
              {t(`checks.status.${editData.status.toLowerCase()}`, editData.status)}
            </span>
          )}
        </div>
      </div>

      <div className="view-content">
        <div className="form-section">
          <h3>{t('checks.view.sections.checkInfo')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('checks.view.fields.checkType')} *</label>
              <select
                value={editData.check_type || ''}
                onChange={(e) => handleFieldChange('check_type', e.target.value)}
                disabled={!createMode}
              >
                <option value="INCOMING">{t('checks.types.incoming')}</option>
                <option value="IN_PROCESS">{t('checks.types.in_process')}</option>
                <option value="FINAL">{t('checks.types.final')}</option>
                <option value="BATCH">{t('checks.types.batch')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('checks.view.fields.status')}</label>
              <select
                value={editData.status || 'PENDING'}
                onChange={(e) => handleFieldChange('status', e.target.value)}
                disabled={!editMode && !createMode}
              >
                <option value="PENDING">{t('checks.status.pending')}</option>
                <option value="PASSED">{t('checks.status.passed')}</option>
                <option value="FAILED">{t('checks.status.failed')}</option>
                <option value="CONDITIONAL">{t('checks.status.conditional')}</option>
                {editData.status === 'CANCELLED' && (
                  <option value="CANCELLED">{t('checks.status.cancelled')}</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label>{t('checks.view.fields.productionOrderLineId')}</label>
              <input
                type="number"
                value={editData.production_order_line_id || ''}
                onChange={(e) => handleFieldChange('production_order_line_id', e.target.value ? parseInt(e.target.value) : null)}
                disabled={(!editMode && !createMode) || !!check?.grn_item_id}
                placeholder={t('checks.view.placeholders.optional')}
              />
            </div>
            <div className="form-group">
              <label>{t('checks.view.fields.inventoryItemId')}</label>
              <input
                type="number"
                value={editData.inventory_item_id || ''}
                onChange={(e) => handleFieldChange('inventory_item_id', e.target.value ? parseInt(e.target.value) : null)}
                disabled={(!editMode && !createMode) || !!check?.grn_item_id}
                placeholder={t('checks.view.placeholders.optional')}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('checks.view.sections.findings')}</h3>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>{t('checks.view.fields.defectsFound')}</label>
              <textarea
                value={editData.defects_found || ''}
                onChange={(e) => handleFieldChange('defects_found', e.target.value)}
                disabled={!editMode && !createMode}
                rows="3"
                placeholder={t('checks.view.placeholders.defects')}
              />
            </div>
            <div className="form-group full-width">
              <label>{t('checks.view.fields.correctiveAction')}</label>
              <textarea
                value={editData.corrective_action || ''}
                onChange={(e) => handleFieldChange('corrective_action', e.target.value)}
                disabled={!editMode && !createMode}
                rows="3"
                placeholder={t('checks.view.placeholders.correctiveAction')}
              />
            </div>
            <div className="form-group full-width">
              <label>{t('checks.view.fields.notes')}</label>
              <textarea
                value={editData.notes || ''}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                disabled={!editMode && !createMode}
                rows="3"
              />
            </div>
          </div>
        </div>

        {check && check.check_date && !createMode && (
          <div className="form-section">
            <h3>{t('checks.view.sections.systemInfo')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('checks.view.fields.checkDate')}</label>
                <span>{formatAppDate(check.check_date, 'datetime')}</span>
              </div>
              {check.checker_name && (
                <div className="info-item">
                  <label>{t('checks.view.fields.checkedBy')}</label>
                  <span>{check.checker_name}</span>
                </div>
              )}
              {check.created_date && (
                <div className="info-item">
                  <label>{t('checks.view.fields.createdDate')}</label>
                  <span>{formatAppDate(check.created_date, 'datetime')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewQCCheck;
